/**
 * Client-side configuration service
 * 
 * Two sources of configuration:
 * 1. Environment variables (VITE_*) - for startup configs (reCAPTCHA, SSE)
 * 2. Backend API (authenticated) - for runtime configs loaded after login
 * 
 * Note: Datadog config is handled directly in constants.ts (needed before auth)
 * 
 * Priority: ENV override > Backend cache > Default value
 */

import { API_BASE_URL } from '../utils/constants';

// Backend config cache (loaded after authentication)
let configCache: Record<string, string | number | boolean | object> | null = null;
let loadingPromise: Promise<void> | null = null;

/**
 * Convert config key to environment variable name
 * e.g., 'datadog.enabled' → 'VITE_DATADOG_ENABLED'
 */
function keyToEnvName(key: string): string {
  return 'VITE_' + key.toUpperCase().replace(/\./g, '_');
}

/**
 * Get environment variable value with type parsing
 */
function getEnvValue(key: string): string | number | boolean | undefined {
  const envName = keyToEnvName(key);
  const envValue = import.meta.env[envName] as string | undefined;
  
  // Return undefined if not set or empty
  if (envValue === undefined || envValue === '') {
    return undefined;
  }

  // Parse boolean values
  if (envValue === 'true') return true;
  if (envValue === 'false') return false;
  
  // Parse numbers
  const numValue = Number(envValue);
  if (!isNaN(numValue) && envValue.trim() !== '') {
    return numValue;
  }

  return envValue;
}

/**
 * Load client configurations from backend (requires authentication)
 * Call this after user login
 */
export async function loadClientConfigs(token: string): Promise<void> {
  // Return existing promise if already loading
  if (loadingPromise) {
    return loadingPromise;
  }

  // Return immediately if already loaded
  if (configCache !== null) {
    return;
  }

  loadingPromise = (async () => {
    try {
      console.log('[ClientConfig] Fetching client configurations...');
      
      const response = await fetch(`${API_BASE_URL}/api/config/client`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.success && result.data) {
        configCache = result.data;
        console.log(`[ClientConfig] Loaded ${Object.keys(result.data).length} configs from backend`);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error) {
      console.warn('[ClientConfig] Failed to fetch configs from backend:', error);
      // Set empty cache - will fall back to env or defaults
      configCache = {};
    } finally {
      loadingPromise = null;
    }
  })();

  return loadingPromise;
}

/**
 * Reload client configurations from backend (force re-fetch)
 * Call this when system_config metadata indicates a change.
 */
export async function reloadClientConfigs(token: string): Promise<void> {
  configCache = null;
  loadingPromise = null;
  await loadClientConfigs(token);
}

/**
 * Get the current config cache object (for context providers)
 */
export function getConfigCache(): Record<string, string | number | boolean | object> {
  return configCache ?? {};
}

/**
 * Clear the config cache (call on logout)
 */
export function clearConfigCache(): void {
  configCache = null;
  loadingPromise = null;
}

/**
 * Check if configs have been loaded from backend
 */
export function isConfigLoaded(): boolean {
  return configCache !== null;
}

/**
 * Get a configuration value
 * Priority: ENV override > Backend cache > Default value
 */
export function getConfig<T = string>(key: string, defaultValue?: T): T {
  // 1. Check env override FIRST (always wins)
  const envValue = getEnvValue(key);
  if (envValue !== undefined) {
    return envValue as T;
  }

  // 2. Try backend cache
  if (configCache && key in configCache) {
    return configCache[key] as T;
  }

  // 3. Return default
  if (defaultValue !== undefined) {
    return defaultValue;
  }

  throw new Error(`Config not found: ${key}. Set ${keyToEnvName(key)} in your .env file or configure in system config.`);
}

/**
 * Get a configuration value, returning undefined if not found
 */
export function getConfigOptional<T = string>(key: string): T | undefined {
  // 1. Check env override FIRST
  const envValue = getEnvValue(key);
  if (envValue !== undefined) {
    return envValue as T;
  }

  // 2. Try backend cache
  if (configCache && key in configCache) {
    return configCache[key] as T;
  }

  return undefined;
}

/**
 * reCAPTCHA configuration (startup config - needed before login)
 * These are read synchronously at app startup for the login page
 */
export function getRecaptchaConfig() {
  return {
    enabled: getConfig<boolean>('recaptcha.enabled', true),
    siteKey: getConfig<string>('recaptcha.site_key', ''),
  };
}

/**
 * SSE configuration (post-login config)
 * Returns the SSE service URL from config, or empty string if not configured
 */
export function getSSEServiceUrl(): string {
  return getConfig<string>('sse.service_url', '');
}
